import { jwtDecode } from "jwt-decode";

/**
 * Single source of truth for Cognito tokens.
 *
 * Every authenticated request asks this module for a token instead of reading a
 * snapshot out of React context. That snapshot was the whole problem: it was
 * taken once at boot and went stale the moment the tab sat idle for an hour.
 */

const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;

/* Refresh this far ahead of the real expiry so a token never dies mid-flight. */
const EXPIRY_SKEW_SECONDS = 120;
const REFRESH_TIMEOUT_MS = 10000;

export const SESSION_EXPIRED_EVENT = "auth:session-expired";
export const TOKENS_UPDATED_EVENT = "auth:tokens-updated";

/** Refresh token is gone or rejected by Cognito — the user must sign in again. */
export class SessionExpiredError extends Error {
	constructor(message = "Session expired") {
		super(message);
		this.name = "SessionExpiredError";
	}
}

/** Refresh could not be reached (offline, timeout). The session is still valid. */
export class RefreshUnavailableError extends Error {
	constructor(message = "Could not reach the auth service") {
		super(message);
		this.name = "RefreshUnavailableError";
	}
}

/* ── storage ──────────────────────────────────────────────────────────────
   "Remember me" decides local vs session storage at sign-in. Resolve it from
   wherever the refresh token actually lives, so a refresh never silently
   migrates a session-only login into localStorage. */

function storageFor(key) {
	try {
		if (localStorage.getItem(key) !== null) return localStorage;
		if (sessionStorage.getItem(key) !== null) return sessionStorage;
	} catch {
		/* storage blocked (private mode, embedded webview) */
	}
	return null;
}

function activeStorage() {
	return storageFor("refreshToken") || localStorage;
}

function read(key) {
	try {
		return localStorage.getItem(key) || sessionStorage.getItem(key) || null;
	} catch {
		return null;
	}
}

export function getStoredTokens() {
	return {
		idToken: read("idToken"),
		accessToken: read("accessToken"),
		refreshToken: read("refreshToken"),
	};
}

export function hasSession() {
	return Boolean(read("refreshToken"));
}

/** Persist a fresh set of tokens and let the rest of the app know. */
export function setTokens({ idToken, accessToken, refreshToken }, { storage } = {}) {
	const target = storage || activeStorage();
	try {
		if (idToken) target.setItem("idToken", idToken);
		if (accessToken) target.setItem("accessToken", accessToken);
		if (refreshToken) target.setItem("refreshToken", refreshToken);
	} catch {
		/* ignore — tokens still work for this page load */
	}

	const tokens = getStoredTokens();
	window.dispatchEvent(new CustomEvent(TOKENS_UPDATED_EVENT, { detail: tokens }));
	return tokens;
}

export function clearTokens() {
	for (const key of ["idToken", "accessToken", "refreshToken", "user", "shop"]) {
		try {
			localStorage.removeItem(key);
			sessionStorage.removeItem(key);
		} catch {
			/* ignore */
		}
	}
}

/* A session can die during boot, before React has mounted anything that could
   hear the event. Park the reason here so the provider can pick it up. */
let pendingExpiryReason = null;

/** Wipe the session and tell the app to drop the user to the sign-in screen. */
export function endSession(reason = "expired") {
	clearTokens();
	inFlightRefresh = null;
	if (reason !== "user-signed-out") pendingExpiryReason = reason;
	window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }));
}

/**
 * An expiry that happened before anything was listening. Reading is pure — it
 * is called from a React state initialiser, which StrictMode runs twice — so
 * clearing it is a separate, explicit step.
 */
export function getPendingExpiry() {
	return pendingExpiryReason;
}

export function clearPendingExpiry() {
	pendingExpiryReason = null;
}

/* ── expiry ──────────────────────────────────────────────────────────────── */

function expiryOf(token) {
	if (!token) return 0;
	try {
		return jwtDecode(token).exp || 0;
	} catch {
		return 0;
	}
}

/** True when the token is expired, unreadable, or about to expire. */
export function isExpiring(token, skewSeconds = EXPIRY_SKEW_SECONDS) {
	const exp = expiryOf(token);
	if (!exp) return true;
	return exp <= Date.now() / 1000 + skewSeconds;
}

/* ── refresh ─────────────────────────────────────────────────────────────── */

let inFlightRefresh = null;

async function requestNewTokens(refreshToken) {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

	let res;
	try {
		res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams({
				grant_type: "refresh_token",
				client_id: CLIENT_ID,
				refresh_token: refreshToken,
			}),
			signal: controller.signal,
		});
	} catch (err) {
		clearTimeout(timer);
		throw new RefreshUnavailableError(err?.name === "AbortError" ? "Token refresh timed out" : "Token refresh could not be sent");
	}

	/* Keep the abort timer armed until the body is parsed — a response whose
	   headers land but whose body stalls would otherwise hang the boot. */
	let data;
	try {
		data = await res.json();
	} catch (err) {
		clearTimeout(timer);
		/* An unreadable body on a 200 is a transport problem, never a reason to
		   throw the user out. */
		if (res.ok) throw new RefreshUnavailableError("Token refresh response could not be read");
		data = {};
	}
	clearTimeout(timer);

	if (!res.ok) {
		/* invalid_grant means the refresh token itself is dead — signing out is
		   the only correct move. Anything else (5xx, throttling) is transient. */
		if (res.status === 400 || res.status === 401 || data?.error === "invalid_grant") {
			throw new SessionExpiredError(data?.error || `Refresh rejected (${res.status})`);
		}
		throw new RefreshUnavailableError(`Refresh failed (${res.status})`);
	}

	if (!data.access_token) {
		throw new SessionExpiredError("Refresh response had no access token");
	}

	return {
		idToken: data.id_token || read("idToken"),
		accessToken: data.access_token,
		/* Cognito does not return a new refresh token on this grant. */
		refreshToken: data.refresh_token || refreshToken,
	};
}

/**
 * Refresh the session. Concurrent callers share one network round trip — the
 * calendar fires several requests at once and they must not stampede.
 */
export function refreshSession() {
	if (inFlightRefresh) return inFlightRefresh;

	const refreshToken = read("refreshToken");
	if (!refreshToken) {
		return Promise.reject(new SessionExpiredError("No refresh token"));
	}

	const storage = activeStorage();

	inFlightRefresh = (async () => {
		try {
			const tokens = await requestNewTokens(refreshToken);
			return setTokens(tokens, { storage });
		} catch (err) {
			if (err instanceof SessionExpiredError) endSession("refresh-rejected");
			throw err;
		} finally {
			inFlightRefresh = null;
		}
	})();

	return inFlightRefresh;
}

/**
 * The main entry point: hand back an access token that is good right now.
 * Returns null when there is no session at all (plain guest browsing).
 */
export async function getValidAccessToken({ forceRefresh = false } = {}) {
	if (!hasSession()) return null;

	const accessToken = read("accessToken");
	if (!forceRefresh && accessToken && !isExpiring(accessToken)) {
		return accessToken;
	}

	const tokens = await refreshSession();
	return tokens.accessToken;
}

/** Same, for the endpoints that still authenticate with the ID token. */
export async function getValidIdToken({ forceRefresh = false } = {}) {
	if (!hasSession()) return null;

	const idToken = read("idToken");
	if (!forceRefresh && idToken && !isExpiring(idToken)) {
		return idToken;
	}

	const tokens = await refreshSession();
	return tokens.idToken;
}

/**
 * Refresh early if the token is close to expiry, without surfacing errors.
 * Used when the tab regains focus after being parked in the background.
 */
export async function revalidateIfStale() {
	if (!hasSession()) return null;
	if (!isExpiring(read("accessToken"))) return read("accessToken");

	try {
		return await getValidAccessToken({ forceRefresh: true });
	} catch {
		/* SessionExpiredError has already fired the logout event. */
		return null;
	}
}

/** Another tab refreshed or signed out — mirror it here. */
export function watchCrossTabAuth() {
	const onStorage = (event) => {
		if (!event.key || !["accessToken", "idToken", "refreshToken"].includes(event.key)) return;

		if (event.key === "refreshToken" && event.newValue === null) {
			window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason: "signed-out-elsewhere" } }));
			return;
		}

		window.dispatchEvent(new CustomEvent(TOKENS_UPDATED_EVENT, { detail: getStoredTokens() }));
	};

	window.addEventListener("storage", onStorage);
	return () => window.removeEventListener("storage", onStorage);
}
